import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Grid,
  Typography,
  Box,
  IconButton,
  CircularProgress,
  Alert,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import MoreHorizIcon from '@mui/icons-material/MoreHoriz';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { PaymentOption } from '../../settings/PaymentSettingsTab';
import salesService, { QuickSaleOtherPaymentWithInvoiceRequest } from '../../../../../services/salesService';
import { CartItem } from '../../types/product.types';
import { FiscalProductItem } from '../../hooks/fiscalDevices/types';
import { getFiscalDevice } from '../../hooks/fiscalDevices';
import PaymentWaitingModal from '../PaymentWaitingModal';
// ✅ Ödeme niyeti: tarayıcı kapansa da sunucu satışı aynı işlem anahtarıyla tamamlar
import {
  newPaymentIntentKey,
  createPaymentIntent,
  reportDeviceResult,
  abandonPaymentIntent,
  isDeviceFailureCertain,
} from '../../../../../services/paymentIntentService';
// ✅ YENİ: İndirim kaydı yardımcıları (fatura kalemi + fiş başlığı için)
import { buildItemDiscountFields, buildHeaderDiscountFields } from '../../utils/quickSaleDiscount';

// ✅ YENİ: Fiş yazdırma için invoice item tipi
interface PrintReceiptItem {
  itemName: string;
  quantity: number;
  unitPrice: number;
  grossTotal: number;
  netTotal: number;
  vatRate: number;
  vatAmount: number;
}

interface OtherPaymentMethodsModalProps {
  open: boolean;
  onClose: () => void;
  otherPaymentOptions: PaymentOption[];
  total: number;
  // Ödeme işlemi için gerekli veriler
  cart: CartItem[];
  companyId: string;
  companyName?: string;
  screenId?: number;
  accountId: number;
  accountName?: string;
  // ✅ Ekran ayarları (depo bilgisi için - nakit ödemedeki gibi)
  screenSettings?: {
    stockVisibility?: {
      warehouses?: string[];
      defaultWarehouseId?: string;
    };
    StockVisibilityJson?: {
      warehouses?: string[];
    };
    useExternalPrinterForPavo?: boolean;
  };
  isRestaurantMode?: boolean;
  cashierId?: number | null;
  cashierName?: string | null;
  createdBy?: string;
  // ✅ YENİ: Sepete uygulanan indirim (fiş başlığına kaydetmek için)
  appliedDiscountType?: 'percentage' | 'amount' | null;
  lastAppliedDiscountAmount?: number;
  // PavoPay yemek karti destegi
  selectedFiscalDevice?: string;
  usePavoPayForOtherPayments?: boolean;
  fiscalProducts?: FiscalProductItem[];
  fiscalTotal?: number;
  // Callbacks
  // ⚠️ paymentLabel: Multinet/Sodexo/Ticket gibi seçilen ödeme yöntemi etiketi.
  // Mutfak ekranı entegrasyonunda OdemeYontemi olarak kullanılır.
  // ✅ Restoran modunda masa kapatma (completeSale) için cashboxId/cashboxName/invoiceNumber da geçirilir.
  onPaymentComplete?: (
    referenceNumber: string,
    successMessage?: string,
    paymentLabel?: string,
    cashboxId?: string,
    cashboxName?: string,
    invoiceNumber?: string,
  ) => void;
  // ✅ YENİ: Fiş yazdırma callback'i (genişletilmiş)
  onPrintReceipt?: (invoiceNumber: string, paymentType: string, items: PrintReceiptItem[]) => void;
  // 🎫 NUMARA GARANTİSİ: Ödeme denemesi başladıktan sonra başarısız/iptal olursa çağrılır.
  // QuickSaleScreen bu callback ile sipariş numarasını yeniler (numara yakılır, tekrar kullanılmaz).
  onPaymentFailed?: () => void;
  // ✅ YENİ: Kulüp girişi modunda dış bilet platformları (FIRSAT ME / BU BİLET) seçilince
  //   satış burada YAPILMAZ; kart okutma + depozito akışı için QuickSaleScreen'e devredilir.
  isClubEntryMode?: boolean;
  onExternalTicketSelected?: (option: PaymentOption) => void;
}

const OtherPaymentMethodsModal: React.FC<OtherPaymentMethodsModalProps> = ({
  open,
  onClose,
  otherPaymentOptions,
  total,
  cart,
  companyId,
  companyName,
  screenId,
  accountId,
  accountName,
  screenSettings,
  isRestaurantMode = false,
  cashierId,
  cashierName,
  createdBy,
  appliedDiscountType = null,
  lastAppliedDiscountAmount = 0,
  selectedFiscalDevice,
  usePavoPayForOtherPayments = false,
  fiscalProducts,
  fiscalTotal,
  onPaymentComplete,
  onPrintReceipt,
  onPaymentFailed,
  isClubEntryMode = false,
  onExternalTicketSelected,
}) => {
  const [isProcessing, setIsProcessing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<{ referenceNumber: string; label: string } | null>(null);
  const [paymentWaitingOpen, setPaymentWaitingOpen] = useState(false);
  const [paymentWaitingStatus, setPaymentWaitingStatus] = useState<'waiting' | 'success' | 'error'>('waiting');
  const [paymentWaitingMessage, setPaymentWaitingMessage] = useState('');

  const MEAL_CARD_LABELS = ['MULTINET', 'METROPOL', 'PLUXEE', 'TICKET', 'SETCARD', 'TOKENFLEX', 'PAYE', 'EDENRED'];
  // ✅ Dış bilet platformları: kulüp girişi modunda kart okutma + depozito akışını tetikler.
  const TICKET_PLATFORM_LABELS = ['FIRSAT ME', 'BU BİLET'];

  // Sadece aktif olan diğer ödeme yöntemlerini filtrele
  const activeOptions = otherPaymentOptions.filter(opt => opt.enabled && opt.type === 'other');

  // Ödeme işlemini gerçekleştir
  const handlePayment = async (option: PaymentOption) => {
    if (cart.length === 0) {
      setError('Sepet boş, ödeme yapılamaz.');
      return;
    }

    if (!option.cashboxId) {
      setError('Bu ödeme yöntemi için kasa tanımlanmamış.');
      return;
    }

    // ✅ KULÜP GİRİŞİ — Dış bilet platformu (FIRSAT ME / BU BİLET): satış burada YAPILMAZ.
    //   Kart okutma + depozito + entitlement akışı için QuickSaleScreen'e devredilir.
    //   Satış (gerçek fiyat, platform kasası, stok, gün sonu) kart okutma sonrası orada yapılır.
    if (isClubEntryMode && onExternalTicketSelected && TICKET_PLATFORM_LABELS.includes(option.label.toUpperCase())) {
      console.log(`🎟️ [DIŞ BİLET] ${option.label} seçildi — kart okutma akışına devrediliyor.`);
      onClose();
      onExternalTicketSelected(option);
      return;
    }

    setIsProcessing(true);
    setError(null);
    setSuccess(null);

    // 🎫 NUMARA GARANTİSİ: Cihaz/API çağrısı yapıldıysa true olur.
    // Validasyon hataları (depo eksik vb.) deneme sayılmaz, numara yakılmaz.
    let attemptStarted = false;

    try {
      console.log(`💳 [DİĞER ÖDEME] ${option.label} ile ödeme başlıyor...`);

      // ✅ İşlem anahtarı ödemenin BAŞINDA üretilir: ödeme niyeti, cihaz sonucu, satış
      // kaydı ve yeniden denemeler aynı anahtarı kullanır.
      const otherIntentKey = newPaymentIntentKey();
      const buildOtherRequest = (): QuickSaleOtherPaymentWithInvoiceRequest => ({
        // ✅ İşlem anahtarı: geçici SQL hatasında otomatik tekrar ve zaman aşımı sonrası
        // "sunucuda kaydedildi mi?" sorgusu (salesService) yalnızca anahtar varsa çalışır.
        IdempotencyKey: otherIntentKey,
        CompanyName: companyName,
        ScreenId: screenId,
        AccountId: accountId,
        AccountName: accountName,
        CashboxId: parseInt(option.cashboxId as string, 10), // yukarıda boş olmadığı doğrulandı
        CashboxName: option.cashboxName,
        TotalAmount: total,
        Currency: 'TRY',
        Description: `${option.label} ile ödeme`,
        CreatedBy: createdBy,
        IsRestaurantMode: isRestaurantMode,
        CashierId: cashierId,
        CashierName: cashierName,
        // Diğer ödeme spesifik alanlar
        OtherPaymentOptionId: option.id,
        OtherPaymentLabel: option.label,
        PaymentProviderType: option.label,
        PaymentProviderName: option.label,
        // ✅ YENİ: Sepete uygulanan indirim kaydı (fiş başlığı)
        ...buildHeaderDiscountFields(cart, appliedDiscountType, lastAppliedDiscountAmount),
        // Sepet ürünleri - nakit ödemedeki gibi depo bilgisi alınır
        Items: cart.map(item => {
          // ✅ Nakit ödemedeki mantık: Önce ürünün deposu, yoksa ekran ayarlarındaki depo
          const itemWarehouseId = (item as any).warehouseId || 
                                  screenSettings?.stockVisibility?.defaultWarehouseId ||
                                  screenSettings?.stockVisibility?.warehouses?.[0] || 
                                  screenSettings?.StockVisibilityJson?.warehouses?.[0] || 
                                  null;
          
          if (!itemWarehouseId) {
            throw new Error(`Ürün için depo bilgisi bulunamadı: ${item.name}. Lütfen ekran ayarlarında depo tanımlayın.`);
          }
          
          return {
            ProductId: item.id,
            ProductName: item.name,
            WarehouseId: itemWarehouseId,
            Quantity: item.quantity,
            Unit: item.unit,
            UnitPrice: item.price,
            VatRate: item.vatRate,
            // KDV dahil/hariç bilgisi
            PriceType: item.vatStatus === 'included' ? 'VAT_INCLUDED' : 'VAT_EXCLUDED',
            HasRecipe: item.hasRecipe || false,
            HasMenu: item.hasMenu || false,
            // Menü opsiyonları
            SelectedOptions: item.selectedOptions?.map(so => ({
              ItemId: so.itemId,
              ItemName: so.itemName,
              Quantity: so.quantity,
              SelectedExtraOption: so.selectedExtraOption ? {
                OptionItemId: so.selectedExtraOption.optionItemId,
                OptionItemName: so.selectedExtraOption.optionItemName,
                PriceDifference: so.selectedExtraOption.priceDifference
              } : undefined
            })),
            TotalPriceDifference: item.totalPriceDifference,
            // ✅ YENİ: İndirim kaydı (fatura kalemi)
            ...buildItemDiscountFields(item)
          };
        })
      });

      const isMealCard = MEAL_CARD_LABELS.includes(option.label.toUpperCase());
      const usesMealCardDevice = isMealCard && usePavoPayForOtherPayments && selectedFiscalDevice === 'pavopay'
        && !!fiscalProducts && fiscalProducts.length > 0;

      // ✅ Ödeme niyeti: cihaz çağrısından (yoksa kayıttan) ÖNCE yazılır (en fazla 4 sn).
      // Yazılamazsa ödeme normal devam eder. İstek oluşturulamazsa (ör. depo yok) atlanır.
      let otherIntentPayload: QuickSaleOtherPaymentWithInvoiceRequest | null = null;
      try { otherIntentPayload = buildOtherRequest(); } catch { otherIntentPayload = null; }
      if (otherIntentPayload) {
        const cashier = (cashierName || '').trim();
        await createPaymentIntent({
          intentKey: otherIntentKey,
          endpoint: 'process-other-payment-with-invoice',
          amount: total,
          paymentKind: 'diger',
          fiscalDevice: usesMealCardDevice ? 'pavopay' : 'yok',
          screenId: screenId ?? null,
          screenMode: isRestaurantMode ? 'restoran' : 'hizli-satis',
          cashierName: cashier && !cashier.includes('@') ? cashier : null,
          payload: otherIntentPayload,
        });
      }
      let mealCardFiscalReference: string | null = null;

      if (usesMealCardDevice && fiscalProducts) {
        console.log(`💳 [DİĞER ÖDEME] PavoPay yemek kartı: ${option.label}`);

        setPaymentWaitingStatus('waiting');
        setPaymentWaitingMessage(`${option.label} ile ödeme bekleniyor...\nLütfen kartı okutunuz.`);
        setPaymentWaitingOpen(true);

        const device = getFiscalDevice('pavopay');
        if (device.processMealCardPayment) {
          attemptStarted = true;
          const fiscalResult = await device.processMealCardPayment(
            fiscalProducts,
            fiscalTotal ?? total,
            option.label,
            undefined,
            companyId,
            screenSettings?.useExternalPrinterForPavo || false
          );
          if (!fiscalResult.success) {
            // Cihaz reddetti/iptal: para kesin alınmadıysa niyet terk edilir (zaman aşımında değil)
            if (isDeviceFailureCertain(fiscalResult.errorMessage)) {
              abandonPaymentIntent(otherIntentKey, `PavoPay yemek kartı: ${fiscalResult.errorMessage || 'başarısız'}`);
            }
            setPaymentWaitingStatus('error');
            setPaymentWaitingMessage(fiscalResult.errorMessage || 'PavoPay yemek kartı ödeme başarısız');
            setTimeout(() => setPaymentWaitingOpen(false), 2000);
            setError(fiscalResult.errorMessage || 'PavoPay yemek kartı ödeme başarısız');
            setIsProcessing(false);
            // 🎫 NUMARA: Deneme başladı ve başarısız oldu — numara yenilensin
            if (onPaymentFailed) onPaymentFailed();
            return;
          }
          mealCardFiscalReference = (fiscalResult as any)?.fiscalData?.SaleNumber || (fiscalResult as any)?.documentId || null;
          setPaymentWaitingStatus('success');
          setPaymentWaitingMessage(`${option.label} ile ödeme alındı!`);
          console.log(`✅ [DİĞER ÖDEME] PavoPay yemek kartı başarılı, SQL kaydı yapılıyor...`);
          setTimeout(() => setPaymentWaitingOpen(false), 1500);
        }
      }

      // Request oluştur (ödeme başında üretilen işlem anahtarıyla)
      const request: QuickSaleOtherPaymentWithInvoiceRequest = buildOtherRequest();

      console.log('💳 [DİĞER ÖDEME] Request:', request);

      // ✅ Para alındı: son isteği niyete bildir (satış kaydından ÖNCE başlar, beklenmez)
      void reportDeviceResult(otherIntentKey, request, {
        fiscalResult: usesMealCardDevice ? 'onaylandi' : 'cihaz-yok',
        fiscalReference: mealCardFiscalReference,
      });

      // API çağrısı
      attemptStarted = true;
      const result = await salesService.processOtherPaymentWithInvoice(request);

      console.log('💳 [DİĞER ÖDEME] Response:', result);

      if (result.Success) {
        console.log(`✅ [DİĞER ÖDEME] ${option.label} ile ödeme başarılı! Ref: ${result.ReferenceNumber}`);

        // ✅ Hemen modalı kapat ve callback çağır (bildirim dışarıda gösterilecek)
        const refNo = result.ReferenceNumber || '';
        const successMsg = `${option.label} ile ödeme başarılı! Ref: ${refNo}`;

        // ✅ YENİ: Fiş yazdır - ödeme türü ve ürün bilgileri ile
        if (onPrintReceipt && refNo) {
          try {
            const printItems: PrintReceiptItem[] = cart.map(item => ({
              itemName: item.name,
              quantity: item.quantity,
              unitPrice: item.price,
              grossTotal: item.price * item.quantity,
              netTotal: item.price * item.quantity,
              vatRate: item.vatRate || 0,
              vatAmount: 0,
            }));
            onPrintReceipt(refNo, option.label, printItems);
            console.log('🖨️ [DİĞER ÖDEME] Fiş yazdırıldı:', option.label);
          } catch (printError) {
            console.error('🖨️ [DİĞER ÖDEME] Fiş yazdırma hatası:', printError);
          }
        }

        // State'leri sıfırla ve hemen kapat
        setSuccess(null);
        setError(null);
        setIsProcessing(false);
        onClose();

        // Callback çağır (bildirim QuickSaleScreen'de gösterilecek)
        // 🍽️ option.label → mutfak entegrasyonunda OdemeYontemi olarak kullanılır.
        // ✅ Restoran modunda masa kapatma (completeSale) için cashbox + invoiceNumber bilgileri geçiriliyor.
        if (onPaymentComplete) {
          onPaymentComplete(
            refNo,
            successMsg,
            option.label,
            option.cashboxId || undefined,
            option.cashboxName || undefined,
            result.InvoiceNumber || undefined,
          );
        }

      } else {
        setError(result.Message || 'Ödeme işlemi başarısız oldu.');
        setIsProcessing(false);
        console.error(`❌ [DİĞER ÖDEME] Hata: ${result.Message}`);
        // 🎫 NUMARA: Deneme başladı ve başarısız oldu — numara yenilensin
        if (onPaymentFailed) onPaymentFailed();
      }

    } catch (err: any) {
      console.error('❌ [DİĞER ÖDEME] Beklenmeyen hata:', err);
      setError(err.message || 'Beklenmeyen bir hata oluştu.');
      setIsProcessing(false);
      // 🎫 NUMARA: Deneme başladıysa (cihaz/API çağrısı yapıldıysa) numara yenilensin
      if (attemptStarted && onPaymentFailed) onPaymentFailed();
    }
  };

  return (
    <>
    <Dialog
      open={open}
      onClose={isProcessing ? undefined : onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 3,
          backgroundColor: '#f8f9fa',
        }
      }}
    >
      <DialogTitle sx={{ 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'space-between',
        bgcolor: 'secondary.main',
        color: 'white',
        py: 1.5,
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <MoreHorizIcon />
          <Typography variant="h6" fontWeight="bold">
            Diğer Ödeme Yöntemleri
          </Typography>
        </Box>
        <IconButton 
          size="small" 
          onClick={onClose} 
          sx={{ color: 'white' }}
          disabled={isProcessing}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ p: 3 }}>
        {/* Başarı Mesajı */}
        {success && (
          <Alert 
            severity="success" 
            icon={<CheckCircleIcon fontSize="inherit" />}
            sx={{ mb: 2 }}
          >
            <Typography variant="body1" fontWeight="bold">
              {success.label} ile ödeme başarılı!
            </Typography>
            <Typography variant="caption">
              Referans: {success.referenceNumber}
            </Typography>
          </Alert>
        )}

        {/* Hata Mesajı */}
        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        {/* Toplam Tutar */}
        <Box sx={{ 
          mb: 3, 
          p: 2, 
          bgcolor: 'white', 
          borderRadius: 2, 
          textAlign: 'center',
          boxShadow: '0 2px 8px rgba(0,0,0,0.08)'
        }}>
          <Typography variant="body2" color="text.secondary">
            Toplam Tutar
          </Typography>
          <Typography variant="h4" fontWeight="bold" color="primary.main">
            {total.toFixed(2)} ₺
          </Typography>
        </Box>

        {/* Ödeme Yöntemleri Grid */}
        {activeOptions.length === 0 ? (
          <Box sx={{ 
            p: 4, 
            textAlign: 'center', 
            bgcolor: 'white', 
            borderRadius: 2,
            border: '2px dashed #e0e0e0'
          }}>
            <MoreHorizIcon sx={{ fontSize: 48, color: 'text.disabled', mb: 1 }} />
            <Typography variant="body1" color="text.secondary">
              Henüz diğer ödeme yöntemi tanımlanmamış.
            </Typography>
            <Typography variant="caption" color="text.disabled">
              Ayarlar → Ödeme sekmesinden "Diğer Ödeme Ekle" butonunu kullanarak ekleyebilirsiniz.
            </Typography>
          </Box>
        ) : (
          <Grid container spacing={2}>
            {activeOptions.map((option) => (
              <Grid item xs={12} sm={6} key={option.id}>
                <Button
                  fullWidth
                  variant="contained"
                  size="large"
                  disabled={isProcessing || cart.length === 0}
                  onClick={() => handlePayment(option)}
                  sx={{
                    py: 2,
                    borderRadius: 2,
                    bgcolor: 'secondary.main',
                    '&:hover': {
                      bgcolor: 'secondary.dark',
                    },
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    gap: 0.5,
                  }}
                >
                  {isProcessing ? (
                    <CircularProgress size={24} color="inherit" />
                  ) : (
                    <>
                      <Typography variant="subtitle1" fontWeight="bold">
                        {option.label}
                      </Typography>
                      {option.cashboxName && (
                        <Typography variant="caption" sx={{ opacity: 0.8 }}>
                          Kasa: {option.cashboxName}
                        </Typography>
                      )}
                    </>
                  )}
                </Button>
              </Grid>
            ))}
          </Grid>
        )}
      </DialogContent>

      <DialogActions sx={{ p: 2, bgcolor: '#f0f0f0' }}>
        <Button 
          onClick={onClose} 
          variant="outlined" 
          color="inherit"
          fullWidth
          disabled={isProcessing}
        >
          Kapat
        </Button>
      </DialogActions>
    </Dialog>

    {/* PavoPay yemek kartı ödeme bekleniyor modalı */}
    <PaymentWaitingModal
      open={paymentWaitingOpen}
      status={paymentWaitingStatus}
      message={paymentWaitingMessage}
    />
    </>
  );
};

export default OtherPaymentMethodsModal;
